import React, { useState, useEffect, useRef } from "react";
import swal from "sweetalert";
import { DatePicker, Select } from 'antd';
import { Form, Button, Spinner } from "react-bootstrap";
import moment from 'moment';
import C from '../lib/Config';

//const PatientStagingDetails = ({ patientId, doctorId, formData, setFormData, viewOnly, stagingData }) => {
  // Create a ref to track if component is mounted
  // const PatientStagingDetails = ({ patientId, doctorId, formData, setFormData, viewOnly, stagingData, diagnosisId }) => {
  //   // ... keep all the existing code ...
  const PatientStagingDetails = ({ patientId, doctorId, formData, setFormData, viewOnly, onCancerStageChange }) => {
  const isMounted = useRef(true);
   // Add a check to determine if we're adding a new diagnosis or editing an existing one
   const isAddMode = !formData?.diagnosisId;
  
  // Set isMounted to false when component unmounts
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // State for staging data
  const [stagingErrors, setStagingErrors] = useState({});
  const [loadingStaging, setLoadingStaging] = useState(false);
  const [currentStagingId, setCurrentStagingId] = useState(null);

  // Fetch staging details when component mounts or patientId changes
  // useEffect(() => {
  //   if (patientId) {
  //     getPatientStaging(patientId);
  //   }
  // }, [patientId]);
  useEffect(() => {
    // Only fetch staging data if we're editing (diagnosisId exists)
    // Don't fetch for new diagnoses
    if (formData && formData.diagnosisId) {
      getStagingByDiagnosisId(formData.diagnosisId);
    }
    // Do NOT fetch by patientId as this would show previous staging data
    // The else condition that was fetching by patientId has been removed
  }, [formData?.diagnosisId]);

  // Define staging options for all users
  const typeOptions = [
    { value: "c", label: "c" },
    { value: "y", label: "y" },
    { value: "p", label: "p" },
    { value: "yc", label: "yc" },
    { value: "yp", label: "yp" },
    // AJCC restaging prefix. Added so an extracted `staging_type: "restaging"`
    // has a target — see docs/report-parser/api-clinical-extraction.md §4.4.
    { value: "r", label: "r" }
  ];

  // Define T options for all users
  const tOptions = [
    { value: "0", label: "0" },
    { value: "1", label: "1" },
    { value: "1a", label: "1a" },
    { value: "1b", label: "1b" },
    { value: "1c", label: "1c" },
    { value: "2", label: "2" },
    { value: "2a", label: "2a" },
    { value: "2b", label: "2b" },
    { value: "2c", label: "2c" },
    { value: "3", label: "3" },
    { value: "3a", label: "3a" },
    { value: "3b", label: "3b" },
    { value: "3c", label: "3c" },
    { value: "4", label: "4" },
    { value: "4a", label: "4a" },
    { value: "4b", label: "4b" },
    { value: "4c", label: "4c" }
  ];

  // Define N options for all users
  const nOptions = [
    { value: "0", label: "0" },
    { value: "1", label: "1" },
    { value: "1a", label: "1a" },
    { value: "1b", label: "1b" },
    { value: "1c", label: "1c" },
    { value: "2", label: "2" },
    { value: "2a", label: "2a" },
    { value: "2b", label: "2b" },
    { value: "2c", label: "2c" },
    { value: "3", label: "3" },
    { value: "3a", label: "3a" },
    { value: "3b", label: "3b" },
    { value: "3c", label: "3c" },
    { value: "4", label: "4" },
    { value: "4a", label: "4a" },
    { value: "4b", label: "4b" },
    { value: "4c", label: "4c" }
  ];

  // Define M options for all users
  const mOptions = [
    { value: "0", label: "0" },
    { value: "1", label: "1" },
    { value: "1a", label: "1a" },
    { value: "1b", label: "1b" },
    { value: "1c", label: "1c" }
  ];

  // Define stage options for all users
  const stageOptions = [
    { value: "I", label: "I" },
    { value: "IA", label: "IA" },
    { value: "IB", label: "IB" },
    { value: "IC", label: "IC" },
    { value: "II", label: "II" },
    { value: "IIA", label: "IIA" },
    { value: "IIB", label: "IIB" },
    { value: "IIC", label: "IIC" },
    { value: "III", label: "III" },
    { value: "IIIA", label: "IIIA" },
    { value: "IIIB", label: "IIIB" },
    { value: "IIIC", label: "IIIC" },
    { value: "IV", label: "IV" },
    { value: "IVA", label: "IVA" },
    { value: "IVB", label: "IVB" },
    { value: "IVC", label: "IVC" }
  ];

  // Function to fetch patient staging details from the API
  const getPatientStagingByPatientId  = async (patientId) => {
    if (!patientId) {
      console.error("Patient ID is undefined");
      return;
    }

    try {
      setLoadingStaging(true);
      const response = await fetch(`${C.getUrl()}/patientStaging/getByPatientId?patientId=${patientId}`);
      const result = await response.json();

      if (result.statusCode === 200 && result.message) {
        const stagings = JSON.parse(result.message);
        const activeStaging = stagings.filter((s) => s.activeInd === "Y");

        if (activeStaging.length > 0) {
          // Get the most recent staging record
          const latestStaging = activeStaging[0]; // Assuming they are sorted by date desc from backend

          // Map staging data to our form format
          const stagingRecord = {
            stagingId: latestStaging.stagingId,
            type: latestStaging.stagingType || "",
            t: latestStaging.tStage || "",
            n: latestStaging.nStage || "",
            m: latestStaging.mStage || "",
            stage: latestStaging.stage || "",
            date: latestStaging.stagingDate ? moment(latestStaging.stagingDate).format("DD-MM-YYYY") : null,
            isSaved: true,
            isModified: false
          };

          if (isMounted.current) {
            // Store the current staging ID for future updates
            setCurrentStagingId(latestStaging.stagingId);
            
            setFormData(prevData => ({
              ...prevData,
              stagingDetails: [stagingRecord]
            }));
          }
        } else {
          // Initialize with empty detail if no active staging exists
          if (isMounted.current) {
            setCurrentStagingId(null);
            
            setFormData(prevData => ({
              ...prevData,
              stagingDetails: [{
                type: '',
                t: '',
                n: '',
                m: '',
                stage: '',
                date: null,
                isSaved: false,
                isModified: false
              }]
            }));
          }
        }
      }
    } catch (error) {
      console.error("Error fetching staging details:", error);
      swal({
        title: "Error!",
        text: "Failed to fetch staging details",
        icon: "error",
        buttons: false,
        timer: 2000
      });
    } finally {
      if (isMounted.current) {
        setLoadingStaging(false);
      }
    }
  };


  // Then add your new function right after it:
const getStagingByDiagnosisId = async (diagnosisId) => {
  if (!diagnosisId) {
    console.error("Diagnosis ID is undefined");
    return;
  }
  try {
    setLoadingStaging(true);
    console.log(`Fetching staging for diagnosis ID: ${diagnosisId}`);
    const response = await fetch(`${C.getUrl()}/patientStaging/getByDiagnosisId?diagnosisId=${diagnosisId}`);
    const result = await response.json();
    if (result.statusCode === 200 && result.message) {
      const stagings = JSON.parse(result.message);
      console.log(`Found ${stagings.length} staging records for diagnosis ID ${diagnosisId}:`, stagings);
      
      // Filter to get active stagings
      const activeStaging = stagings.filter((s) => s.activeInd === "Y");
      if (activeStaging.length > 0) {
        // Get the most recent staging record
        const latestStaging = activeStaging[0]; 
        // Map staging data to form format
        const stagingRecord = {
          stagingId: latestStaging.stagingId,
          type: latestStaging.stagingType || "",
          t: latestStaging.tStage || "",
          n: latestStaging.nStage || "",
          m: latestStaging.mStage || "",
          stage: latestStaging.stage || "",
          date: latestStaging.stagingDate ? moment(latestStaging.stagingDate).format("DD-MM-YYYY") : null,
          isSaved: true,
          isModified: false
        };
        if (isMounted.current) {
          setCurrentStagingId(latestStaging.stagingId);
          
          // Update the form data with staging details
          setFormData(prevData => ({
            ...prevData,
            stagingDetails: [stagingRecord]
          }));
        }
      } else {
        // Initialize with empty detail if no active staging exists
        if (isMounted.current) {
          setCurrentStagingId(null);
          
          setFormData(prevData => ({
            ...prevData,
            stagingDetails: [{
              type: '',
              t: '',
              n: '',
              m: '',
              stage: '',
              date: null,
              isSaved: false,
              isModified: false
            }]
          }));
        }
      }
    }
  } catch (error) {
    console.error(`Error fetching staging details for diagnosis ID ${diagnosisId}:`, error);
  } finally {
    if (isMounted.current) {
      setLoadingStaging(false);
    }
  }
};
  // Handle staging changes
  const handleStagingChange = (field, value) => {
    const updatedStagingDetails = [...formData.stagingDetails];
    updatedStagingDetails[0] = {
      ...updatedStagingDetails[0],
      [field]: value,
      isModified: true
    };

    setFormData(prevData => ({
      ...prevData,
      stagingDetails: updatedStagingDetails
    }));

    // Clear errors for this field
    if (stagingErrors[field]) {
      setStagingErrors({
        ...stagingErrors,
        [field]: ''
      });
    }
  };

  // Handle date change
  const handleDateChange = (date) => {
    const formattedDate = date ? date.format('DD-MM-YYYY') : null;
    console.log(`Date changed to: ${formattedDate}`);
    handleStagingChange('date', formattedDate);
  };

  // Validate staging details
  const validateStagingDetails = () => {
    let isValid = true;
    let newErrors = {};
    
    // Make sure formData and stagingDetails exist
    //if (!formData || !formData.stagingDetails || !Array.isArray(formData.stagingDetails) || formData.stagingDetails.length === 0) {
      if (!formData || !formData.stagingDetails || !Array.isArray(formData.stagingDetails) || formData.stagingDetails.length === 0) {  
    console.error("Invalid staging details data structure");
      swal({
        title: "Error!",
        text: "Invalid staging data structure. Please refresh the page and try again.",
        icon: "error",
        buttons: false,
        timer: 2000
      });
      return false;
    }
    
    const detail = formData.stagingDetails[0];
    
    // Only validate if modified or not saved
    if (detail.isModified || !detail.isSaved) {
      // Validation for all fields
      if (!detail.type) {
        newErrors.type = 'Type is required';
        isValid = false;
      }
      
      if (!detail.t) {
        newErrors.t = 'T is required';
        isValid = false;
      }
      
      if (!detail.n) {
        newErrors.n = 'N is required';
        isValid = false;
      }
      
      if (!detail.m) {
        newErrors.m = 'M is required';
        isValid = false;
      }
      
      if (!detail.stage) {
        newErrors.stage = 'Stage is required';
        isValid = false;
      }
      
      if (!detail.date) {
        newErrors.date = 'Date is required';
        isValid = false;
      } else if (detail.date && !moment(detail.date, ["DD-MM-YYYY", "YYYY-MM-DD"], true).isValid()) {
        newErrors.date = 'Invalid date format';
        isValid = false;
      }
    }
    
    setStagingErrors(newErrors);
    
    if (!isValid) {
      swal({
        title: "Validation Error!",
        text: "Please fill in all required fields correctly.",
        icon: "error",
        buttons: false,
        timer: 2000,
      });
    }
    
    return isValid;
  };

  // Save staging details
  const saveStagingDetails = async () => {
    try {
      // Validate the staging details first
      if (!validateStagingDetails()) {
        return;
      }
      
      // Make sure formData and stagingDetails exist
      if (!formData || !formData.stagingDetails || !Array.isArray(formData.stagingDetails) || formData.stagingDetails.length === 0) {
        throw new Error("Invalid staging data structure");
      }
      
      const detail = formData.stagingDetails[0];
      
      // Only proceed if the record has been modified or is new
      if (!detail.isModified && detail.isSaved) {
        swal({
          title: "Info",
          text: "No changes detected to save",
          icon: "info",
          buttons: false,
          timer: 2000
        });
        return;
      }
      
      setLoadingStaging(true);
      
      if (!patientId) {
        throw new Error('Patient ID is required');
      }
      
      // First, if there's a current staging ID, deactivate it
      if (currentStagingId) {
        console.log(`Deactivating existing staging record: ${currentStagingId}`);
        
        const deactivateUrl = `${C.getUrl()}/patientStaging/deactivate?stagingId=${currentStagingId}`;
        
        const deactivateResponse = await fetch(deactivateUrl, {
          method: 'POST', // Using POST as specified in the controller
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (!deactivateResponse.ok) {
          throw new Error(`Failed to deactivate previous staging: ${deactivateResponse.statusText}`);
        }
        
        console.log("Successfully deactivated previous staging record");
      }
      
      // Format date correctly for API
      let formattedDate = null;
      if (detail.date) {
        try {
          // Try multiple date formats to ensure proper parsing
          const parsedDate = moment(detail.date, ["DD-MM-YYYY", "YYYY-MM-DD", "MM-DD-YYYY"]);
          if (parsedDate.isValid()) {
            formattedDate = parsedDate.format("YYYY-MM-DD");
            console.log(`Parsed date from '${detail.date}' to '${formattedDate}'`);
          } else {
            console.warn(`Could not parse date: ${detail.date}`);
          }
        } catch (dateError) {
          console.error("Error parsing date:", dateError);
        }
      }
      
      // Make sure we have a valid numeric patientId
      const numericPatientId = parseInt(patientId, 10);
      if (isNaN(numericPatientId)) {
        throw new Error(`Invalid patient ID: ${patientId}`);
      }
      
      // Prepare data for API - always create a new record
      // const stagingData = {
      //   patientId: numericPatientId,
      //   activeInd: 'Y',
      //   stagingType: detail.type || '',
      //   tStage: detail.t || '',
      //   nStage: detail.n || '',
      //   mStage: detail.m || '',
      //   stage: detail.stage || '',
      //   stagingDate: formattedDate,
      //   createdById: parseInt(localStorage.getItem('userId') || doctorId || 1, 10),
      //   updatedById: parseInt(localStorage.getItem('userId') || doctorId || 1, 10),
      //   createdDate: new Date().toISOString(),
      //   updatedDate: new Date().toISOString()
      // };
      // In the saveStagingDetails function, update the stagingData object:
const stagingData = {
  patientId: numericPatientId,
  diagnosisId: formData.diagnosisId || null, // Link to the diagnosis
  activeInd: 'Y',
  stagingType: detail.type || '',
  tStage: detail.t || '',
  nStage: detail.n || '',
  mStage: detail.m || '',
  stage: detail.stage || '',
  stagingDate: formattedDate,
  createdById: parseInt(localStorage.getItem('userId') || doctorId || 1, 10),
  updatedById: parseInt(localStorage.getItem('userId') || doctorId || 1, 10),
  createdDate: new Date().toISOString(),
  updatedDate: new Date().toISOString()
};
      
      console.log("Creating new staging record:", stagingData);
      
      // Send the request to create the new record
      const response = await fetch(C.getUrl() + "/patientStaging/save", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(stagingData)
      });
      
      // Get the raw response text first
      const responseText = await response.text();
      console.log("Raw response:", responseText);
      
      // Try to parse as JSON
      let result;
      try {
        result = JSON.parse(responseText);
        console.log("Parsed response:", result);
      } catch (parseError) {
        console.error("Error parsing response as JSON:", parseError);
        throw new Error(`Server response is not valid JSON: ${responseText}`);
      }
      
      // Check if the response is successful
      if (!response.ok || result.statusCode !== 200) {
        const errorMsg = result.message || result.debugMessage || `Failed with status: ${response.status}`;
        throw new Error(errorMsg);
      }
      
      // Get the new staging ID
      if (result.message) {
        try {
          // The message might be a string that needs parsing
          const messageData = typeof result.message === 'string' ? JSON.parse(result.message) : result.message;
          
          if (messageData && messageData.stagingId) {
            // Update the current staging ID
            setCurrentStagingId(messageData.stagingId);
            console.log("New staging ID assigned:", messageData.stagingId);
          }
        } catch (err) {
          console.error("Error extracting staging ID from response:", err);
        }
      }
      
      // Update local state to reflect changes
      setFormData(prevData => ({
        ...prevData,
        stagingDetails: [{
          ...detail,
          isModified: false,
          isSaved: true
        }]
      }));
      
      // Show success message
      swal({
        title: "Success!",
        text: "Staging details saved successfully",
        icon: "success",
        buttons: false,
        timer: 2000
      });
      
      // Refresh data from server to ensure we have the latest
      //await getPatientStaging(patientId);
      // After successful save, refresh based on diagnosisId if available
if (formData && formData.diagnosisId) {
  await getStagingByDiagnosisId(formData.diagnosisId);
} else {
  await getPatientStagingByPatientId(patientId);
}
      
    } catch (err) {
      console.error("Error saving staging details:", err);
      swal({
        title: "Error!",
        text: "Failed to save staging details: " + (err.message || 'Unknown error'),
        icon: "error",
        buttons: false,
        timer: 2000
      });
    } finally {
      if (isMounted.current) {
        setLoadingStaging(false);
      }
    }
  };

  //return (
    // <div className="row mb-3">
    //   <div className="col-12">
    //     <strong>Staging</strong>
    //     {viewOnly ? (
    //       // View-only mode display
    //       <div className="table-responsive">
    //         {(formData.stagingDetails && formData.stagingDetails.length > 0) || (stagingData && stagingData.length > 0) ? (
    //           <table className="table table-bordered table-sm">
    //             <thead>
    //               <tr>
    //                 <th>TYPE</th>
    //                 <th>T</th>
    //                 <th>N</th>
    //                 <th>M</th>
    //                 <th>STAGE</th>
    //                 <th>DATE</th>
    //               </tr>
    //             </thead>
    //             <tbody>
    //               {(formData.stagingDetails && formData.stagingDetails.length > 0 ? 
    //                 formData.stagingDetails : 
    //                 stagingData
    //               ).map((staging, index) => (
    //                 <tr key={`staging-view-${index}`}>
    //                   <td>{staging.type || staging.stagingType || "N/A"}</td>
    //                   <td>{staging.t || staging.tStage || "N/A"}</td>
    //                   <td>{staging.n || staging.nStage || "N/A"}</td>
    //                   <td>{staging.m || staging.mStage || "N/A"}</td>
    //                   <td>{staging.stage || "N/A"}</td>
    //                   <td>{staging.date || (staging.stagingDate ? moment(staging.stagingDate).format("DD-MM-YYYY") : "N/A")}</td>
    //                 </tr>
    //               ))}
    //             </tbody>
    //           </table>
    //         ) : (
    //           <div className="alert alert-info mt-2">No staging information available.</div>
    //         )}
    //       </div>
    //     ) : (
    //       // Edit mode display - only one row
    //       <div className="table-responsive">
    //         <table className="table table-bordered border">
    //           <thead>
    //             <tr>
    //               <th style={{ minWidth: '80px' }}>TYPE<span className="text-danger">*</span></th>
    //               <th style={{ minWidth: '80px' }}>T<span className="text-danger">*</span></th>
    //               <th style={{ minWidth: '80px' }}>N<span className="text-danger">*</span></th>
    //               <th style={{ minWidth: '80px' }}>M<span className="text-danger">*</span></th>
    //               <th style={{ minWidth: '80px' }}>STAGE<span className="text-danger">*</span></th>
    //               <th style={{ minWidth: '120px' }}>DATE<span className="text-danger">*</span></th>
    //             </tr>
    //           </thead>
    //           <tbody>
    //             {formData.stagingDetails && formData.stagingDetails.length > 0 && (
    //               <tr className="row-with-content">
    return (
      <div className="row mb-3">
        <div className="col-12">
          {/* dx-subtitle matches the sibling sub-headings (Lymph Node, Primary
              Tumor Size) inside the section this renders into. The rule lives in
              DiagnosisManagement.css, scoped to .diagnosis-container — this
              component is only ever rendered there, and the class is inert
              anywhere else. */}
          <span className="dx-subtitle">Staging</span>
          {viewOnly ? (
            // View-only mode display - unchanged
            <div className="table-responsive">
              {/* Existing code for view-only mode */}
            </div>
          ) : (
            // Edit mode display - only show stored data for edit mode, not for add mode
            <div className="table-responsive">
              <table className="table table-bordered border">
                <thead>
                  <tr>
                    <th style={{ minWidth: '80px' }}>TYPE<span className="text-danger">*</span></th>
                    <th style={{ minWidth: '80px' }}>T<span className="text-danger">*</span></th>
                    <th style={{ minWidth: '80px' }}>N<span className="text-danger">*</span></th>
                    <th style={{ minWidth: '80px' }}>M<span className="text-danger">*</span></th>
                    <th style={{ minWidth: '80px' }}>STAGE<span className="text-danger">*</span></th>
                    <th style={{ minWidth: '120px' }}>DATE<span className="text-danger">*</span></th>
                  </tr>
                </thead>
                <tbody>
                  {formData.stagingDetails && formData.stagingDetails.length > 0 && (
                    <tr className="row-with-content">
                    <td>
                      <Form.Control
                        as="select"
                        value={formData.stagingDetails[0].type || ""}
                        onChange={(e) => handleStagingChange('type', e.target.value)}
                        className={stagingErrors.type ? "is-invalid" : ""}
                      >
                        <option value="">Select Type</option>
                        {typeOptions.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </Form.Control>
                      {stagingErrors.type && (
                        <div className="invalid-feedback">{stagingErrors.type}</div>
                      )}
                    </td>
                    <td>
                      <Form.Control
                        as="select"
                        value={formData.stagingDetails[0].t || ""}
                        onChange={(e) => handleStagingChange('t', e.target.value)}
                        className={stagingErrors.t ? "is-invalid" : ""}
                      >
                        <option value="">Select T</option>
                        {tOptions.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </Form.Control>
                      {stagingErrors.t && (
                        <div className="invalid-feedback">{stagingErrors.t}</div>
                      )}
                    </td>
                    <td>
                      <Form.Control
                        as="select"
                        value={formData.stagingDetails[0].n || ""}
                        onChange={(e) => handleStagingChange('n', e.target.value)}
                        className={stagingErrors.n ? "is-invalid" : ""}
                      >
                        <option value="">Select N</option>
                        {nOptions.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </Form.Control>
                      {stagingErrors.n && (
                        <div className="invalid-feedback">{stagingErrors.n}</div>
                      )}
                    </td>
                    <td>
                      <Form.Control
                        as="select"
                        value={formData.stagingDetails[0].m || ""}
                        onChange={(e) => handleStagingChange('m', e.target.value)}
                        className={stagingErrors.m ? "is-invalid" : ""}
                      >
                        <option value="">Select M</option>
                        {mOptions.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </Form.Control>
                      {stagingErrors.m && (
                        <div className="invalid-feedback">{stagingErrors.m}</div>
                      )}
                    </td>
                    <td>
                      <Form.Control
                        as="select"
                        value={formData.stagingDetails[0].stage || ""}
                        onChange={(e) => handleStagingChange('stage', e.target.value)}
                        className={stagingErrors.stage ? "is-invalid" : ""}
                      >
                        <option value="">Select Stage</option>
                        {stageOptions.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </Form.Control>
                      {stagingErrors.stage && (
                        <div className="invalid-feedback">{stagingErrors.stage}</div>
                      )}
                    </td>
                    <td>
                      <DatePicker
                        className={`form-control ${stagingErrors.date ? "is-invalid" : ""}`}
                        value={formData.stagingDetails[0].date ? moment(formData.stagingDetails[0].date, ["DD-MM-YYYY", "YYYY-MM-DD"], true) : null}
                        onChange={handleDateChange}
                        format="DD-MM-YYYY"
                        placeholder="Select date"
                      />
                      {stagingErrors.date && (
                        <div className="invalid-feedback">{stagingErrors.date}</div>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Save button - only show in edit mode 
        {!viewOnly && (
          <div className="row mt-3 mb-4">
            <div className="col-12 d-flex justify-content-center">
              <Button 
                variant="secondary"
                onClick={saveStagingDetails}
                disabled={loadingStaging}
                className="px-4"
              >
                {loadingStaging ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Saving...
                  </>
                ) : 'Save Staging'}
              </Button>
            </div>
          </div>
         )}*/}
      </div>
    </div>
  );
};

export default PatientStagingDetails;